import { type ReactNode, useEffect } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn'

interface Props {
  open: boolean
  onClose: () => void
  title: string
  subtitle?: string
  children: ReactNode
  footer?: ReactNode
  size?: 'md' | 'lg' | 'xl' | '2xl' | '3xl'
}

const SIZES: Record<NonNullable<Props['size']>, string> = {
  md: 'max-w-md', lg: 'max-w-lg', xl: 'max-w-xl', '2xl': 'max-w-2xl', '3xl': 'max-w-3xl',
}

/**
 * Painel lateral (sheet) genérico — para formulários longos sem ocupar a tela.
 * Cabeçalho fixo + corpo rolável + rodapé fixo (ações).
 */
export function Sheet({ open, onClose, title, subtitle, children, footer, size = '2xl' }: Props) {
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [open])

  return (
    <div className={cn('fixed inset-0 z-50', open ? 'visible' : 'invisible')}>
      <div className={cn('absolute inset-0 bg-black/30 transition-opacity', open ? 'opacity-100' : 'opacity-0')} onClick={onClose} />
      <aside className={cn(
        'absolute right-0 top-0 h-full w-full bg-white shadow-xl flex flex-col transition-transform duration-200',
        SIZES[size], open ? 'translate-x-0' : 'translate-x-full',
      )}>
        <div className="flex items-start justify-between gap-3 px-5 py-3.5 border-b border-gray-100 shrink-0">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-gray-800 break-words">{title}</h2>
            {subtitle && <p className="text-[11px] text-gray-500 break-words">{subtitle}</p>}
          </div>
          <button onClick={onClose} aria-label="Fechar" className="p-2 -mr-2 text-gray-400 hover:text-gray-600 cursor-pointer shrink-0"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
        {footer && <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 shrink-0">{footer}</div>}
      </aside>
    </div>
  )
}
